// oxlint-disable-next-line no-restricted-imports
import { Combobox as MantineCombobox, InputBase, useCombobox } from "@mantine/core";
import { IconChevronDown, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { fontSizes } from "#app/theme/v2/fontSizes";
import { fontWeights } from "#app/theme/v2/fontWeights";
import { lineHeights } from "#app/theme/v2/lineHeights";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";

import classes from "./MultiSelect.module.css";

type MultiSelectProps = {
  label?: string;
  placeholder?: string;
  data: { value: string; label: string }[];
  error?: string;
  disabled?: boolean;
  value?: string[];
  onChange?: (value: string[]) => void;
  searchable?: boolean;
  clearable?: boolean;
  nothingFoundMessage?: string;
};

const MultiSelect = ({
  label,
  placeholder,
  data,
  error,
  disabled,
  value = [],
  onChange,
  searchable = true,
  clearable,
  nothingFoundMessage,
}: MultiSelectProps) => {
  const { t } = useTranslation("components");
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex("active"),
  });

  const [search, setSearch] = useState("");

  const selectedLabels = value.map((v) => data.find((d) => d.value === v)?.label ?? v).join(", ");

  const filteredOptions =
    searchable && search.trim()
      ? data.filter((item) => item.label.toLowerCase().includes(search.toLowerCase().trim()))
      : data;

  const handleOptionSubmit = (val: string) => {
    const isSelected = value.includes(val);
    onChange?.(isSelected ? value.filter((v) => v !== val) : [...value, val]);
    combobox.resetSelectedOption();
    setSearch("");
  };

  const displayValue = combobox.dropdownOpened ? search : selectedLabels;
  const placeholderText = placeholder ?? t("select.placeholder");
  const activePlaceholder = combobox.dropdownOpened
    ? selectedLabels || placeholderText
    : placeholderText;
  const showClear = clearable && value.length > 0;

  return (
    <MantineCombobox
      store={combobox}
      onOptionSubmit={handleOptionSubmit}
      shadow="none"
      radius={radius.lg}
      classNames={{
        dropdown: classes.dropdown,
        option: classes.option,
      }}
    >
      <MantineCombobox.Target>
        <InputBase
          label={label}
          placeholder={activePlaceholder}
          error={error}
          disabled={disabled}
          value={displayValue}
          rightSection={
            showClear ? (
              <IconX
                size={12}
                color={colors.icon.primaryDefault}
                style={{ cursor: "pointer" }}
                onClick={() => onChange?.([])}
              />
            ) : (
              <IconChevronDown size={12} color={colors.icon.primaryDefault} />
            )
          }
          rightSectionPointerEvents={showClear ? "all" : "none"}
          pointer={!combobox.dropdownOpened}
          onChange={(event) => {
            if (!searchable) {
              return;
            }
            setSearch(event.currentTarget.value);
            combobox.openDropdown();
            combobox.resetSelectedOption();
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => {
            combobox.openDropdown();
            setSearch("");
          }}
          onBlur={() => {
            combobox.closeDropdown();
            setSearch("");
          }}
          classNames={{
            label: classes.label,
            input: classes.input,
            section: classes.section,
          }}
          style={{
            "--select-padding": `${spacing[2]} ${spacing[9]} ${spacing[2]} ${spacing[2]}`,
            "--select-label-fz": fontSizes[14],
            "--select-label-fw": fontWeights.medium,
            "--select-label-lh": lineHeights[1.25],
            "--select-label-c": colors.text.primaryDefault,
            "--select-label-mb": spacing[1],
            "--select-input-fz": fontSizes[14],
            "--select-input-fw": fontWeights.regular,
            "--select-input-h": spacing[9],
            "--select-input-radius": radius.xs,
            "--select-input-bg": disabled
              ? colors.bg.elevation.tertiary
              : colors.bg.elevation.primary,
            "--select-input-bd": error ? colors.border.error.primary : colors.border.tertiary,
            "--select-input-c": colors.text.primaryDefault,
            "--select-placeholder-c": colors.text.secondaryDefault,
            "--select-focus-bd": colors.border.primaryDefault,
            "--select-error-bd": colors.border.error.primary,
            "--select-disabled-bg": colors.bg.elevation.tertiary,
            "--select-disabled-c": colors.text.primaryDisabled,
          }}
        />
      </MantineCombobox.Target>

      <MantineCombobox.Dropdown>
        <MantineCombobox.Options>
          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            {filteredOptions.length === 0 ? (
              <MantineCombobox.Empty>
                {nothingFoundMessage ?? t("select.nothingFound")}
              </MantineCombobox.Empty>
            ) : (
              filteredOptions.map((item) => (
                <MantineCombobox.Option
                  key={item.value}
                  value={item.value}
                  active={value.includes(item.value)}
                >
                  {item.label}
                </MantineCombobox.Option>
              ))
            )}
          </div>
        </MantineCombobox.Options>
      </MantineCombobox.Dropdown>
    </MantineCombobox>
  );
};

export { MultiSelect };
